import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container-app py-4">
          <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors w-fit">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      <main className="container-app py-12 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center">
              <FileText className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-4xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                TERMS & CONDITIONS
              </h1>
              <p className="text-muted-foreground">Last updated: February 2026</p>
            </div>
          </div>

          <div className="prose prose-invert max-w-none space-y-8">
            
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>1. INTRODUCTION</h2>
              <p className="text-muted-foreground mb-4">
                Welcome to The Hotshot Marketplace ("THM", "we", "us", or "our"). These Terms and Conditions ("Terms") govern your use of our website located at www.thehotshotmarketplace.com.au and our services (collectively, the "Platform").
              </p>
              <p className="text-muted-foreground mb-4">
                By accessing or using our Platform, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Platform.
              </p>
              <p className="text-muted-foreground">
                The Hotshot Marketplace is an online platform that connects customers who need freight transported ("Customers") with independent transport providers ("Providers"). We do not provide transport services directly; we facilitate connections between Customers and Providers.
              </p>
            </section>

            {/* User Accounts */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>2. USER ACCOUNTS</h2>
              
              <h3 className="text-lg font-semibold mb-2">2.1 Account Registration</h3>
              <p className="text-muted-foreground mb-4">
                To use certain features of our Platform, you must register for an account. You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate, current, and complete.
              </p>
              
              <h3 className="text-lg font-semibold mb-2">2.2 Account Types</h3>
              <p className="text-muted-foreground mb-4">
                <strong>Customer Accounts:</strong> Free to create and use. Customers can post jobs and receive quotes from Providers.
              </p>
              <p className="text-muted-foreground mb-4">
                <strong>Provider Accounts:</strong> Require a subscription after the free trial period. Providers can browse jobs and submit quotes.
              </p>
              
              <h3 className="text-lg font-semibold mb-2">2.3 Account Security</h3>
              <p className="text-muted-foreground">
                You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.
              </p>
            </section>

            {/* Provider Subscriptions */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>3. PROVIDER SUBSCRIPTIONS & FEES</h2>
              
              <h3 className="text-lg font-semibold mb-2">3.1 Free Trial</h3>
              <p className="text-muted-foreground mb-4">
                New Provider accounts receive a <strong>14-day free trial</strong>. During the trial period, you have full access to browse jobs and submit quotes. Payment details are collected at registration but you will not be charged during the trial period.
              </p>
              
              <h3 className="text-lg font-semibold mb-2">3.2 Subscription Fee</h3>
              <p className="text-muted-foreground mb-4">
                After the free trial ends, your subscription will automatically convert to a paid subscription at <strong>$9.95 AUD per week</strong>. Your registered payment method will be charged automatically.
              </p>
              
              <h3 className="text-lg font-semibold mb-2">3.3 Platform Fee (Booking Fee)</h3>
              <div className="bg-primary/10 border border-primary/30 rounded-sm p-4 mb-4">
                <p className="text-foreground font-medium">
                  A <strong>10% platform fee</strong> is deducted from the total job value when a job is completed. This fee is taken from the customer's payment before the remaining amount is paid to the Provider.
                </p>
              </div>
              <p className="text-muted-foreground mb-4">
                <strong>Example:</strong> If a customer pays $100 for a job, the Provider receives $90 and THM retains $10 as the platform fee.
              </p>
              <p className="text-muted-foreground">
                The platform fee applies to all completed jobs, including those completed during the free trial period. Providers should factor this fee into their quotes.
              </p>
              
              <h3 className="text-lg font-semibold mb-2 mt-4">3.4 Cancellation</h3>
              <p className="text-muted-foreground mb-4">
                You may cancel your subscription at any time through your account dashboard. Upon cancellation:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4">
                <li>You will retain access until the end of your current billing period or trial period</li>
                <li>No further charges will be made to your payment method</li>
                <li>Any jobs in progress can still be completed</li>
                <li>You can reactivate your subscription at any time</li>
              </ul>
              
              <h3 className="text-lg font-semibold mb-2">3.5 Refunds</h3>
              <p className="text-muted-foreground">
                Subscription fees are non-refundable. If you cancel mid-billing period, you will not receive a prorated refund but will maintain access until the end of your paid period.
              </p>
            </section>

            {/* Payment Terms */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>4. PAYMENT TERMS</h2>
              
              <h3 className="text-lg font-semibold mb-2">4.1 Customer Payments</h3>
              <p className="text-muted-foreground mb-4">
                When a Customer accepts a Provider's quote, payment is processed through our secure payment system (powered by Stripe). Funds are held until the job is marked as complete.
              </p>
              
              <h3 className="text-lg font-semibold mb-2">4.2 Provider Payouts</h3>
              <p className="text-muted-foreground mb-4">
                Providers receive payment (minus the 10% platform fee) after job completion. Payouts are processed through Stripe Connect to the Provider's registered bank account.
              </p>
              
              <h3 className="text-lg font-semibold mb-2">4.3 Payment Processing</h3>
              <p className="text-muted-foreground">
                All payments are processed securely through Stripe. By using our Platform, you agree to Stripe's terms of service. We do not store your full credit card details on our servers.
              </p>
            </section>

            {/* Provider Responsibilities */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>5. PROVIDER RESPONSIBILITIES</h2>
              <p className="text-muted-foreground mb-4">As a Provider, you agree to:</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2">
                <li>Hold all necessary licenses, permits, and insurance required to operate your transport business in Australia</li>
                <li>Provide accurate information about your services, equipment, and availability</li>
                <li>Honour quotes provided to Customers unless exceptional circumstances arise</li>
                <li>Complete accepted jobs in a professional and timely manner</li>
                <li>Communicate promptly with Customers regarding job status and any issues</li>
                <li>Maintain appropriate insurance coverage for goods in transit</li>
                <li>Comply with all applicable laws and regulations</li>
                <li>Not engage in any fraudulent, misleading, or deceptive conduct</li>
              </ul>
            </section>

            {/* Customer Responsibilities */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>6. CUSTOMER RESPONSIBILITIES</h2>
              <p className="text-muted-foreground mb-4">As a Customer, you agree to:</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2">
                <li>Provide accurate information about the freight to be transported, including dimensions, weight, and any special requirements</li>
                <li>Ensure goods are properly packaged and ready for collection at the specified time</li>
                <li>Not request transport of prohibited, illegal, or dangerous goods</li>
                <li>Be available or have a representative available for pickup and delivery</li>
                <li>Pay for accepted quotes promptly</li>
                <li>Communicate any changes or issues to the Provider promptly</li>
              </ul>
            </section>

            {/* Prohibited Items */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>7. PROHIBITED ITEMS</h2>
              <p className="text-muted-foreground mb-4">The following items are prohibited from transport through our Platform:</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2">
                <li>Illegal substances or contraband</li>
                <li>Weapons, firearms, or explosives</li>
                <li>Hazardous materials (unless Provider is specifically licensed)</li>
                <li>Live animals (unless Provider is specifically equipped)</li>
                <li>Stolen goods</li>
                <li>Any items prohibited by Australian law</li>
              </ul>
            </section>

            {/* Limitation of Liability */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>8. LIMITATION OF LIABILITY</h2>
              
              <h3 className="text-lg font-semibold mb-2">8.1 Platform Role</h3>
              <p className="text-muted-foreground mb-4">
                The Hotshot Marketplace is a platform that connects Customers and Providers. We are not a transport company and do not provide transport services directly. We are not responsible for:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4">
                <li>The quality, safety, or legality of services provided by Providers</li>
                <li>The accuracy of information provided by Customers or Providers</li>
                <li>Loss, damage, or delay to goods in transit</li>
                <li>Disputes between Customers and Providers</li>
              </ul>
              
              <h3 className="text-lg font-semibold mb-2">8.2 Maximum Liability</h3>
              <p className="text-muted-foreground">
                To the maximum extent permitted by law, our total liability to you for any claims arising from your use of the Platform is limited to the amount of fees you have paid to us in the 12 months preceding the claim.
              </p>
            </section>

            {/* Disputes */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>9. DISPUTES</h2>
              
              <h3 className="text-lg font-semibold mb-2">9.1 Between Users</h3>
              <p className="text-muted-foreground mb-4">
                Disputes between Customers and Providers should be resolved directly between the parties. While we may assist in facilitating communication, we are not obligated to resolve disputes and any resolution is at our sole discretion.
              </p>
              
              <h3 className="text-lg font-semibold mb-2">9.2 With THM</h3>
              <p className="text-muted-foreground">
                Any disputes with The Hotshot Marketplace should be raised through our support channels. We will endeavour to resolve issues fairly and promptly. These Terms are governed by the laws of Queensland, Australia.
              </p>
            </section>

            {/* Termination */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>10. TERMINATION</h2>
              <p className="text-muted-foreground mb-4">
                We reserve the right to suspend or terminate your account at any time if you:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4">
                <li>Violate these Terms</li>
                <li>Engage in fraudulent or illegal activity</li>
                <li>Receive multiple complaints from other users</li>
                <li>Fail to pay subscription fees (for Providers)</li>
              </ul>
              <p className="text-muted-foreground">
                Upon termination, you will lose access to your account and any data associated with it.
              </p>
            </section>

            {/* Changes to Terms */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>11. CHANGES TO TERMS</h2>
              <p className="text-muted-foreground">
                We may update these Terms from time to time. We will notify you of any material changes by posting the new Terms on our Platform and updating the "Last updated" date. Your continued use of the Platform after changes constitutes acceptance of the revised Terms.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>12. CONTACT US</h2>
              <p className="text-muted-foreground mb-4">
                If you have any questions about these Terms, please contact us:
              </p>
              <div className="bg-muted/30 rounded-sm p-4">
                <p className="text-foreground font-medium">The Hotshot Marketplace</p>
                <p className="text-muted-foreground">Email: Admin@thehotshotmarketplace.com.au</p>
                <p className="text-muted-foreground">Website: www.thehotshotmarketplace.com.au</p>
              </div>
            </section>

          </div>

          {/* Footer Note */}
          <div className="mt-12 pt-8 border-t border-border">
            <p className="text-sm text-muted-foreground text-center">
              By using The Hotshot Marketplace, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
            </p>
          </div>

        </motion.div>
      </main>
    </div>
  );
}
